import {useParams} from 'react-router-dom';
import {
  useMailCampaignQuery,
  MailCampaignProspectSearchConditionAttributes,
  useUpdateMailCampaignProspectSearchConditionMutation,
  useCurrentUserQuery,
} from 'api';
import {useRecoilValue} from 'recoil';
import totalCountAtom from '../../../totalCountAtom';

export default () => {
  const count = useRecoilValue(totalCountAtom);

  const {mailCampaignId} = useParams<{
    mailCampaignId: string;
  }>();

  const {data: {mailCampaign: {searchCondition = {}, status, pool} = {}} = {}} =
    useMailCampaignQuery({
      variables: {uuid: mailCampaignId},
      skip: !mailCampaignId,
    });

  const {data: {currentUser} = {}} = useCurrentUserQuery();

  // HubSpot コンタクトの条件は段階リリース中のため、当面は管理者にのみ表示する。
  // クライアントの owner を含む useClientUser の isAdmin ではなく、
  // Leadpad 側の管理者だけに限定している。
  const canUseHubspotContactFilter = currentUser?.role === 'admin';

  const [update] = useUpdateMailCampaignProspectSearchConditionMutation({
    refetchQueries: ['prospectPoolsByMailCampaign'],
  });

  const isUpdatable = status === 'draft';

  const setCondition = (
    condition: MailCampaignProspectSearchConditionAttributes,
  ) =>
    update({
      variables: {
        uuid: mailCampaignId,
        searchCondition: {
          ...condition,
        },
      },
    });

  const clearCondition = () =>
    setCondition({
      users: [],
      leadSources: [],
      originalLeadSources: [],
      stages: [],
      ranks: [],
      tags: [],
      prospectCustomizeItemSearchConditions: [],
      collectedFormUrls: [],
      forms: [],
      cities: [],
      subCategories: [],
      listingMarkets: [],
      employeeNumber: {},
      capitalFund: {},
      excludedTags: [],
      hubspotDealPipelineId: null,
      hubspotDealPipelineStageIds: [],
      hubspotContactSearchConditions: [],
      salesforceLeadRecordTypeId: null,
      salesforceContactRecordTypeId: null,
      salesforceLeadFieldSelectStatusIds: [],
      salesforceContactSearchConditions: [],
      inflowDateType: 'latest',
      inflowDateFrom: null,
      inflowDateTo: null,
      clientPhaseProspects: [],
    });

  const numberOfLeadFiltersInUse = [
    searchCondition.users?.length > 0,
    searchCondition.leadSources?.length > 0,
    searchCondition.originalLeadSources?.length > 0,
    searchCondition.stages?.length > 0,
    searchCondition.ranks?.length > 0,
    searchCondition.tags?.length > 0,
    searchCondition.excludedTags?.length > 0,
    searchCondition.prospectCustomizeItemSearchConditions?.length > 0,
    searchCondition.inflowDateFrom || searchCondition.inflowDateTo,
    searchCondition?.clientPhaseProspects?.length > 0,
  ].filter(Boolean).length;

  const numberOfFormFiltersInUse = [
    searchCondition.collectedFormUrls?.length > 0,
    searchCondition.forms?.length > 0,
  ].filter(Boolean).length;

  const numberOfCompanyFiltersInUse = [
    searchCondition.cities?.length > 0,
    searchCondition.subCategories?.length > 0,
    searchCondition.listingMarkets?.length > 0,
    searchCondition.employeeNumber?.from || searchCondition?.employeeNumber?.to,
    searchCondition.capitalFund?.from || searchCondition?.capitalFund?.to,
  ].filter(Boolean).length;

  const numberOfHubspotFiltersInUse = [
    searchCondition.hubspotDealPipelineId,
    canUseHubspotContactFilter &&
      searchCondition.hubspotContactSearchConditions?.length > 0,
  ].filter(Boolean).length;

  const numberOfSalesforceFiltersInUse = [
    searchCondition.salesforceLeadRecordTypeId,
    searchCondition.salesforceContactRecordTypeId,
  ].filter(Boolean).length;

  const numberOfFiltersInUse =
    numberOfLeadFiltersInUse +
    numberOfFormFiltersInUse +
    numberOfCompanyFiltersInUse +
    numberOfHubspotFiltersInUse +
    numberOfSalesforceFiltersInUse;

  return {
    condition: searchCondition,
    poolId: pool?.uuid,
    canUseHubspotContactFilter,
    count,
    isUpdatable,
    setCondition,
    clearCondition,
    numberOfFiltersInUse,
    numberOfLeadFiltersInUse,
    numberOfFormFiltersInUse,
    numberOfCompanyFiltersInUse,
    numberOfHubspotFiltersInUse,
    numberOfSalesforceFiltersInUse,
  };
};
